import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { KindChip } from "../components/UpdatesPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["All", "Research Question", "Satellite Data", "Partnerships", "Technology", "Expedition Planning"];

export default function Updates() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/updates`)
      .then((r) => setUpdates(r.data))
      .catch(() => setUpdates([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (filter === "All" ? updates : updates.filter((u) => u.category === filter)),
    [updates, filter]
  );

  return (
    <main className="min-h-screen bg-midnight" data-testid="updates-page">
      <header className="border-b border-polar/10 bg-abyss/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 h-[68px] flex items-center justify-between">
          <Link to="/" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-polar hover:text-ice transition-colors" data-testid="updates-back-link">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Mission Home
          </Link>
          <span className="font-mono text-[10px] tracking-[0.3em] text-tech hidden sm:block">ARJUNA ANTARCTICA · DISPATCHES</span>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-mission" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-polar">Editorial</span>
          </div>
          <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ice leading-[0.95]" data-testid="editorial-page-header">
            Mission <span className="text-polar">updates</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-ice/65 leading-relaxed">
            Research question development, satellite-data preparation, partner announcements, technical demonstrations
            and expedition planning — published as clearly labelled concept notes until genuine field results exist.
          </p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2" role="group" aria-label="Filter updates by category">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              data-testid={`editorial-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                filter === f ? "border-mission bg-mission text-ice" : "border-polar/20 text-tech hover:border-polar/50 hover:text-polar"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-16 font-mono text-xs tracking-[0.3em] text-tech" data-testid="updates-loading">RECEIVING TRANSMISSION…</div>
        ) : visible.length === 0 ? (
          <div className="mt-16 border border-polar/15 bg-[#0B1F35]/70 p-10 max-w-xl" data-testid="updates-empty">
            <p className="text-sm text-ice/60 leading-relaxed">
              No updates in this category yet. Genuine mission progress will be published here as it happens.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {visible.map((u, i) => (
              <motion.article
                key={u.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
                className="border border-polar/15 bg-[#0B1F35]/80 p-7 flex flex-col hover:border-polar/45 transition-colors duration-300"
                data-testid={`editorial-dispatch-card-${i + 1}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <KindChip kind={u.kind} />
                  <span className="font-mono text-[10px] text-tech tracking-widest">{u.date}</span>
                </div>
                <h2 className="font-display font-semibold text-2xl text-ice mt-5 tracking-wide leading-tight">{u.title}</h2>
                <p className="text-sm text-ice/60 mt-3 leading-relaxed flex-1">{u.summary}</p>
                <div className="mt-6 font-mono text-[10px] tracking-[0.25em] text-tech uppercase">{u.category}</div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
